import { useEffect, useState } from "react";
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  TextField,
} from "@mui/material";
import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";
import { get, put } from "src/utils/networkUtils";
import { createBlobInContainer } from "src/utils/azureBlob";
import useWithUrlTenant from "src/hooks/useTenant";
import { getEnabledLangs } from "src/langs/langs";
import LanguageTabs from "src/components/language/LanguageTabs";

const LANGS = getEnabledLangs();

export default function AppComponentsPage() {
  const withTenant = useWithUrlTenant();
  const [componentRows, setComponentRows] = useState(null);
  const [components, setComponents] = useState(null);
  const [updatingComponent, setUpdatingComponent] = useState(null);
  const [activeLangTab, setActiveLangTab] = useState(0);
  const defaultLocale = process.env.REACT_APP_DEFAULT_LOCALE || 'en';
  const activeLang = LANGS[activeLangTab]?.value ?? "en";

  useEffect(() => {
    get(withTenant(`/admin/app-components`)).then(function (response) {
      setComponents(response.componentTypes);
      setComponentRows(response.appComponents);
    });
  }, [withTenant]);

  const updateComponentHandler = (row) => {
    console.log("Updated app component", row);
    setUpdatingComponent(row.AppComponentId);

    const imageUrl = row.ImageUrlI18n?.[activeLang] ?? row.ImageUrl ?? "";
    const rowToSend = {
      ...row,
      Title: row.TitleI18n?.[defaultLocale] || row.Title,
      Description: row.DescriptionI18n?.[defaultLocale] || row.Description,
      ImageUrl: row.ImageUrlI18n?.[defaultLocale] || row.ImageUrl,
      ImageI18n: { [activeLang]: imageUrl },
    };
    if (rowToSend.isNew) {
      rowToSend.AppComponentId = null;
      delete rowToSend.isNew;
    }

    put(withTenant(`/admin/app-components`), rowToSend)
      .then(function (response) {
        setUpdatingComponent(null);
        get(withTenant(`/admin/app-components`)).then(function (response) {
          setComponents(response.componentTypes);
          setComponentRows(response.appComponents);
        });
      })
      .catch(function (error) {
        setUpdatingComponent(null);
        console.error("Error updating component:", error);
      });
  };

  const addNewComponent = () => {
    // Get all existing component types
    const existingTypes = componentRows.map((row) => row.Type);

    // Find available component types
    const availableTypes = components.filter(
      (type) => !existingTypes.includes(type.value)
    );

    if (availableTypes.length === 0) {
      // Show error - all component types already exist
      alert("All component types already exist. Cannot add more components.");
      return;
    }

    // Create new row with first available type
    // Use a temporary negative ID for new rows to track loading state
    const newRow = {
      AppComponentId: -(componentRows.length + 1),
      Title: "",
      TitleI18n: { en: "", fr: "" },
      Description: "",
      DescriptionI18n: { en: "", fr: "" },
      ImageUrl: "",
      ImageUrlI18n: { en: "", fr: "" },
      ClickUrl: "",
      Status: 0, // default to hidden
      Type: availableTypes[0].value,
      CountryCode: "ALL", // default to ALL
      isNew: true, // Flag to indicate this is a new component
    };

    setComponentRows([...componentRows, newRow]);
  };

  const uploadFiles = (event, rowIndex) => {
    if (event.target.files.length > 0) {
      const files = event.target.files;
      const activeLang = LANGS[activeLangTab]?.value ?? "en";

      let uploadPromises = [];
      uploadPromises.push(createBlobInContainer(files[0]));

      if (uploadPromises.length > 0) {
        const uploadFiles = async () => {
          await Promise.all(uploadPromises);

          const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
          const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
          const imageUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${files[0].name}`;

          const updatedRows = [...componentRows];
          updatedRows[rowIndex] = {
            ...updatedRows[rowIndex],
            ImageUrlI18n: {
              ...(updatedRows[rowIndex].ImageUrlI18n || {}),
              [activeLang]: imageUrl,
            },
            ...({ ImageUrl: imageUrl }),
          };
          setComponentRows(updatedRows);
        };

        uploadFiles().catch(console.error);
      }
    }
  };

  return (
    <Container>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={5}
        mt={5}
      >
        <Typography variant="h4" gutterBottom>
          App Components
        </Typography>
        <Button
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={addNewComponent}
          disabled={componentRows === null || components === null}
        >
          Add App Component
        </Button>
      </Stack>

      <Card>
        {componentRows === null || components === null ? (
          <Box
            sx={{ display: "flex", justifyContent: "center" }}
            style={{ padding: 20 }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
              <LanguageTabs
                langs={LANGS}
                value={activeLangTab}
                onChange={(_, newValue) => setActiveLangTab(newValue)}
                ariaLabel="app components language tabs"
                idPrefix="app-components-language-tab"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              />
            </Box>
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title ({LANGS[activeLangTab]?.label})</TableCell>
                      <TableCell>Description ({LANGS[activeLangTab]?.label})</TableCell>
                      <TableCell>Image ({LANGS[activeLangTab]?.label})</TableCell>
                      <TableCell>Click URL</TableCell>
                      <TableCell>Visible</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Country Code</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {componentRows.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={row.TitleI18n?.[activeLang] ?? row.Title ?? ""}
                            onChange={(e) => {
                              const updatedRows = [...componentRows];
                              updatedRows[index] = {
                                ...updatedRows[index],
                                TitleI18n: {
                                  ...(updatedRows[index].TitleI18n || {}),
                                  [activeLang]: e.target.value,
                                },
                                ...({ Title: e.target.value }),
                              };
                              setComponentRows(updatedRows);
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={row.DescriptionI18n?.[activeLang] ?? row.Description ?? ""}
                            onChange={(e) => {
                              const updatedRows = [...componentRows];
                              updatedRows[index] = {
                                ...updatedRows[index],
                                DescriptionI18n: {
                                  ...(updatedRows[index].DescriptionI18n || {}),
                                  [activeLang]: e.target.value,
                                },
                                ...({ Description: e.target.value }),
                              };
                              setComponentRows(updatedRows);
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="primary"
                            aria-label="upload picture"
                            component="label"
                          >
                            <input
                              hidden
                              accept="image/*"
                              type="file"
                              onChange={(event) => uploadFiles(event, index)}
                            />
                            <Iconify
                              icon={"eva:upload-outline"}
                              sx={{ mr: 2 }}
                            />
                          </IconButton>
                          {row.ImageUrlI18n?.[activeLang] ?? row.ImageUrl ? (
                            <img
                              style={{
                                margin: "0 auto",
                                width: 100,
                                background: "grey",
                              }}
                              alt="Product"
                              src={row.ImageUrlI18n?.[activeLang] ?? row.ImageUrl}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={row.ClickUrl || ""}
                            onChange={(e) => {
                              const updatedRows = [...componentRows];
                              updatedRows[index].ClickUrl = e.target.value;
                              setComponentRows(updatedRows);
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            fullWidth
                            variant="standard"
                            value={row.Status === 0 ? "false" : "true"}
                            onChange={(e) => {
                              const updatedRows = [...componentRows];
                              updatedRows[index].Status =
                                e.target.value === "true" ? 1 : 0;
                              setComponentRows(updatedRows);
                            }}
                          >
                            <MenuItem value="true">True</MenuItem>
                            <MenuItem value="false">False</MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            fullWidth
                            variant="standard"
                            value={row.Type}
                            onChange={(e) => {
                              const updatedRows = [...componentRows];
                              updatedRows[index].Type = e.target.value;
                              setComponentRows(updatedRows);
                            }}
                          >
                            {components &&
                              components.map((type, idx) => (
                                <MenuItem key={idx} value={type.value}>
                                  {type.name}
                                </MenuItem>
                              ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            fullWidth
                            variant="standard"
                            value={row.CountryCode || "ALL"}
                            onChange={(e) => {
                              const updatedRows = [...componentRows];
                              updatedRows[index].CountryCode = e.target.value.toUpperCase();
                              setComponentRows(updatedRows);
                            }}
                          >
                            <MenuItem value="ALL">All</MenuItem>
                            <MenuItem value="US">US</MenuItem>
                            <MenuItem value="CA">CA</MenuItem>
                            <MenuItem value="OTHERS">Others</MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            onClick={() => updateComponentHandler(row)}
                            disabled={updatingComponent === row.AppComponentId}
                          >
                            {updatingComponent === row.AppComponentId ? (
                              <CircularProgress size={24} />
                            ) : (
                              "Update"
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          </>
        )}
      </Card>
    </Container>
  );
}
